import { useSuspenseQuery } from '@tanstack/react-query';
import Axios from 'api/client';
import { ErrorCodes } from 'constants/error-codes';
import { IS_PRODUCTION } from 'constants/runtime';
import { useAppState } from 'customHooks/useAppState';
import { useLogout } from 'customHooks/useLogout';
import { GlobalAbortController } from 'helpers/abort-controller';
import { opsQueryKeys } from 'queries/ops-query-keys';
import { GivebutterAxiosError } from 'types/error';

const TEST_BYPASS_HEADER = 'x-butter-noratelimit';

const useAxiosInterceptors = () => {
    const { apiUrl } = useAppState();
    const { mutate: logout } = useLogout();

    useSuspenseQuery({
        queryKey: opsQueryKeys.axiosInterceptors(),
        queryFn: () => {
            Axios.interceptors.response.use(
                response => response,
                (error: GivebutterAxiosError) => {
                    if (error?.request?.responseURL?.includes(apiUrl)) {
                        const isAuthError =
                            error?.response?.status === ErrorCodes.UNAUTHORIZED &&
                            !error.response?.data?.two_factor;
                        if (isAuthError) {
                            logout();
                            return Promise.reject(error);
                        }
                    }

                    return Promise.reject(error);
                },
            );

            Axios.interceptors.request.use(async config => {
                if (!config.signal) {
                    config.signal = GlobalAbortController.signal;
                }

                return config;
            });

            if (!IS_PRODUCTION) {
                Axios.interceptors.request.use(
                    config => {
                        config.headers[TEST_BYPASS_HEADER] = true;
                        return config;
                    },
                    null,
                    { synchronous: true },
                );
            }

            return Promise.resolve(true);
        },
    });
};

export { useAxiosInterceptors };
